<template>
  <div id="page-retailer-user-list">
    <div>
      <vs-button @click="addUsers" type="border" class="mb-6 mr-6">{{
        this.$t("RetailerUser.AddUsers")
      }}</vs-button>
      <vs-button @click="inviteUser" type="border" class="mb-6">{{
        this.$t("RetailerUser.InviteUser")
      }}</vs-button>
      <h4 class="mb-5">{{ $t("Retailer.Users") }}</h4>
      <div class="flex flex-wrap items-center">
        <dx-data-grid
          class="dx-card wide-card"
          :data-source="customDataSource"
          :focused-row-index="0"
          :show-borders="false"
          :focused-row-enabled="true"
          :column-auto-width="true"
          :column-hiding-enabled="true"
          :allow-column-reordering="true"
          :allow-column-resizing="true"
          :ref="gridRefKey"
        >
          >
          <dx-state-storing
            :enabled="true"
            type="custom"
            :custom-load="loadState"
            :custom-save="saveState"
            :storage-key="storageKey"
          />
          <dx-paging
            :page-size="20"
            :show-page-size-selector="false"
            :show-info="true"
          />
          <dx-editing
            :allow-updating="true"
            :use-icons="true"
            :allow-deleting="true"
            mode="popup"
          >
            <dx-grid-popup
              :show-title="true"
              :width="600"
              height="auto"
              :title="$t('RetailerUser.Edit')"
            >
              <dx-position my="center" at="center" of="window" />
            </dx-grid-popup>
            <dx-form>
              <dx-item :col-count="2" :col-span="2" item-type="group">
                <dx-item data-field="isOwner" :col-span="2" />
              </dx-item>
            </dx-form>
          </dx-editing>
          <dx-pager :show-page-size-selector="true" :show-info="true" />
          <dx-filter-row :visible="false" />
          <dx-column :width="110" :buttons="editButtons" type="buttons" />
          <dx-column
            data-field="id"
            caption="Id"
            data-type="number"
            :width="90"
            :hiding-priority="1"
            :visible="false"
          />

          <dx-column
            data-field="idCustomer"
            caption="IdCustomer"
            :hiding-priority="2"
            :visible="false"
          />
          <dx-column
            data-field="displayName"
            :caption="this.$t('RetailerUser.Name')"
            :hiding-priority="99"
          />
          <dx-column
            data-field="email"
            :caption="this.$t('RetailerUser.Email')"
            :hiding-priority="98"
          />
          <dx-column
            data-field="isOwner"
            data-type="boolean"
            :caption="this.$t('RetailerUser.IsOwner')"
            :hiding-priority="97"
          />
        </dx-data-grid>
      </div>
    </div>
    <dx-popup
      :title="this.$t('RetailerUser.AddUsersToRetailer')"
      v-bind:visible.sync="addUsersPopupActive"
      :width="600"
      height="auto"
      :drag-enabled="true"
      :close-on-outside-click="true"
      :show-close-button="true"
      :show-title="true"
    >
      <retailer-user-add
        :showPopup.sync="addUsersPopupActive"
        :idRetailer="idRetailer"
        :idOrganisation="idOrganisation"
      />
    </dx-popup>

    <dx-popup
      :key="retailerUserInviteKey"
      v-bind:visible.sync="inviteUserPopupActive"
      :drag-enabled="false"
      :close-on-outside-click="true"
      :show-title="true"
      :show-close-button="true"
      :width="800"
      height="auto"
      :title="this.$t('RetailerUser.InviteUser')"
    >
      <retailer-user-invite
        :showPopup.sync="inviteUserPopupActive"
        :idRetailer="idRetailer"
        :idOrganisation="idOrganisation"
      />
    </dx-popup>
  </div>
</template>

<script>
import moduleRetailerManagement from '@/store/retailer-management/moduleRetailerManagement.js'
import CustomStore from 'devextreme/data/custom_store'
import RetailerUserInvite from './RetailerUserInvite'
import RetailerUserAdd from './RetailerUserAdd'
import { DxPopup } from 'devextreme-vue/popup'
import { notifyMixin } from '@/mixins/notifyMixin'

import DxDataGrid, {
  DxColumn,
  DxFilterRow,
  DxLookup,
  DxPager,
  DxPaging,
  DxEditing,
  DxPosition,
  DxForm,
  DxStateStoring,
  DxPopup as DxGridPopup
} from 'devextreme-vue/data-grid'

import { DxItem } from 'devextreme-vue/form'

const gridRefKey = "grid-retailer-users"
export default {
  name: 'retailer-user-list',
  mixins: [notifyMixin],
  components: {
    DxDataGrid,
    DxColumn,
    DxFilterRow,
    DxLookup,
    DxPager,
    DxPaging,
    DxEditing,
    DxPopup,
    DxPosition,
    DxForm,
    DxItem,
    DxGridPopup,
    RetailerUserInvite,
    RetailerUserAdd,
    DxStateStoring
  },
  props: {
    masterDetailData: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    const customDataSource = new CustomStore({
      key: 'id',
      load: () => {
        return this.$store
          .dispatch(
            'retailerManagement/fetchRetailerUsers',
            this.idRetailer
          )
          .then((res) => {
            return res.data.items
          })
          .catch((error) => {
            this.notifyError(error)
          })
      },
      update: (key, values) => {
        return this.$store
          .dispatch('retailerManagement/saveRetailerUser', {
            id: key,
            ...values
          })
          .then(() => {
            this.notifySuccess(this.$t('RecordUpdated'))
          })
          .catch((error) => {
            this.notifyError(error)
          })
      },
      remove: (key) => {
        return this.$store
          .dispatch('retailerManagement/deleteRetailerUser', key)
          .then(() => {
            this.notifySuccess(this.$t('RecordDeleted'))
          })
          .catch((error) => {
            this.notifyError(error)
          })
      }
    })

    const editButtons = ['edit', 'delete']

    return {
      addUsersPopupActive: false,
      inviteUserPopupActive: false,
      customDataSource,
      gridRefKey,
      retailerUserInviteKey: 1,
      editButtons,
      storageKey: 'dx_datagrid_gridUser_1',
      restoreFullGridState: false
    }
  },
  watch: {
    inviteUserPopupActive: function (val) {
      console.log(`inviteUserPopupActive changed to ${val}`)
      if (!val) {
        this.retailerUserInviteKey += 1
        this.refreshGrid()
      }
    },
    addUsersPopupActive: function (val) {
      console.log(`addUsersPopupActive changed to ${val}`)
      if (!val) {
        this.refreshGrid()
      }
    }
  },
  computed: {
    roles () {
      return this.$store.state.refCodes.roles.map(a => {
        return { ...a, description: this.$t(`RoleList.${a.description}`) }
      })
    },
    idRetailer () { return this.masterDetailData.key },
    idOrganisation () { return this.masterDetailData.data.idOrganisation }
  },
  methods: {
    addUsers () {
      this.$vs.loading()
      this.$store
        .dispatch(
          'retailerManagement/fetchUnmappedUsers',
          { idRetailer: this.idRetailer, idOrganisation: this.masterDetailData.data.idOrganisation }
        )
        .then(() => {
          this.addUsersPopupActive = true
          this.$vs.loading.close()
        })
        .catch((error) => {
          this.notifyError(error)
          this.$vs.loading.close()
        })
    },
    inviteUser () {
      this.inviteUserPopupActive = true
    },
    refreshGrid () {
      const grid = this.$refs[gridRefKey].instance
      grid.refresh()
    },
    loadState () {
      const storage = localStorage[this.storageKey]
      if (storage) {
        const state = JSON.parse(storage)

        if (this.restoreFullGridState) {
          return state
        } else {
          const res = {
            columns: state.columns,
            pageSize: state.pageSize
          }
          for (let i = 0; i < res.columns.length; i++) {
            delete res.columns[i].filterValue
          }
          return res
        }
      }
    },
    saveState (state) {
      localStorage[this.storageKey] = JSON.stringify(state)
    }
  },
  mounted () { },
  created () {
    if (!moduleRetailerManagement.isRegistered) {
      this.$store.registerModule(
        'retailerManagement',
        moduleRetailerManagement
      )
      moduleRetailerManagement.isRegistered = true
    }

  }
}
</script>

<style lang="scss">
#page-retailer-user-list {
  .retailer-list-filters {
    .vs__actions {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-58%);
    }
  }
}
</style>